import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { HistoryService } from '../../../services/history/history.service';
import { IoService } from '../../../services/io/io.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { LocationService } from '../../../services/location/location.service';
import { ReportService } from '../../../services/report/report.service'
import { AppComponent } from 'src/app/app.component';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';
import { CustomerMergeComponent } from '../customer-merge/customer-merge.component';

@Component({
  selector: 'app-detail-customer',
  templateUrl: './detail-customer.component.html',
  styleUrls: ['./detail-customer.component.scss']
})
export class DetailCustomerComponent implements OnInit {

  documentId$: Observable<any>;
  documentId;

  showBackButton;

  user;

  // True when the loaded record was a blank stub from the add flow (no identity
  // fields yet) — used to clean it up when the operator switches to an existing
  // record after a duplicate match.
  isNewRecord = false;

  states = [
    { name: 'Alabama', key: 'AL' },
    { name: 'Alaska', key: 'AK' },
    { name: 'Arizona', key: 'AZ' },
    { name: 'Arkansas', key: 'AR' },
    { name: 'California', key: 'CA' },
    { name: 'Colorado', key: 'CO' },
    { name: 'Connecticut', key: 'CT' },
    { name: 'Delaware', key: 'DE' },
    { name: 'Florida', key: 'FL' },
    { name: 'Georgia', key: 'GA' },
    { name: 'Hawaii', key: 'HI' },
    { name: 'Idaho', key: 'ID' },
    { name: 'Illinois', key: 'IL' },
    { name: 'Indiana', key: 'IN' },
    { name: 'Iowa', key: 'IA' },
    { name: 'Kansas', key: 'KS' },
    { name: 'Kentucky', key: 'KY' },
    { name: 'Louisiana', key: 'LA' },
    { name: 'Maine', key: 'ME' },
    { name: 'Maryland', key: 'MD' },
    { name: 'Massachusetts', key: 'MA' },
    { name: 'Michigan', key: 'MI' },
    { name: 'Minnesota', key: 'MN' },
    { name: 'Mississippi', key: 'MS' },
    { name: 'Missouri', key: 'MO' },
    { name: 'Montana', key: 'MT' },
    { name: 'Nebraska', key: 'NE' },
    { name: 'Nevada', key: 'NV' },
    { name: 'New Hampshire', key: 'NH' },
    { name: 'New Jersey', key: 'NJ' },
    { name: 'New Mexico', key: 'NM' },
    { name: 'New York', key: 'NY' },
    { name: 'North Carolina', key: 'NC' },
    { name: 'North Dakota', key: 'ND' },
    { name: 'Ohio', key: 'OH' },
    { name: 'Oklahoma', key: 'OK' },
    { name: 'Oregon', key: 'OR' },
    { name: 'Pennsylvania', key: 'PA' },
    { name: 'Rhode Island', key: 'RI' },
    { name: 'South Carolina', key: 'SC' },
    { name: 'South Dakota', key: 'SD' },
    { name: 'Tennessee', key: 'TN' },
    { name: 'Texas', key: 'TX' },
    { name: 'Utah', key: 'UT' },
    { name: 'Vermont', key: 'VT' },
    { name: 'Virginia', key: 'VA' },
    { name: 'Washington', key: 'WA' },
    { name: 'West Virginia', key: 'WV' },
    { name: 'Wisconsin', key: 'WI' },
    { name: 'Wyoming', key: 'WY' }
  ];

  private permissionsLoaded: Subscription;
  private detailDocumentSet: Subscription;

  constructor(
    private locationR: Location,
    private route: ActivatedRoute,
    private router: Router,
    private historyService: HistoryService,
    private authorizationService: AuthorizationService,
    private locationService: LocationService,
    private ioService: IoService,
    private reportService: ReportService
  ) { }

  ngOnInit() {
    this.documentId$ = this.route.paramMap.pipe(switchMap(params => of(params.get('documentId'))));
    this.documentId$.subscribe(documentId => {
      if (documentId) {
        this.documentId = documentId;
        this.init();
      }
    });

    if (this.historyService.getPreviousLinkDepth() > 3) {
      this.showBackButton = true;
    }

  }
  close() {
    this.reportService.closeDetailPanel(this.route);
  }

  back() {
    this.locationR.back();
  }

  loadDetail(type, id) {
    console.log('loadDetail');
    this.reportService.navigateWithinReport(['../../'+type+'/'+id], this.route);
  }

  init = async () => {
    console.log('detail-user init: ');

    this.user = null;

    // Get General User Data
    let user: any = await this.ioService.post('/customer/getCustomer', {
      customerId: this.documentId
    });

    user.activeLocation = null;

    // See if we can manage this user
    let authenticatedUser = this.authorizationService.getUser();
    user.managableLocations = []

    if (user.locationId) {
      for (let locationId of user.locationId) {
        if (this.authorizationService.checkPermission('manageCustomers', locationId)) {
          user.canManage = true;
          break;
        }
      }

      // Merging requires its own dedicated permission at any of the
      // customer's locations (enforced server-side as well).
      for (let locationId of user.locationId) {
        if (this.authorizationService.checkPermission('mergeCustomers', locationId)) {
          user.canMerge = true;
          break;
        }
      }
    }

    // Get User Authorizations
    if (user.canManage) {
      for (let location of this.locationService.getLocations()) {
        if (location._id) {
          if (user.locationId.indexOf(location._id) != -1) {
            location.active = true;
          }
          user.managableLocations.push(location);
        }
      }
    }

    // Sort managable locations
    user.managableLocations.sort((a, b) => (a.name > b.name) ? 1 : (a.name === b.name) ? ((a.active > b.active) ? 1 : -1) : -1 );

    if (!user.address) {
      user.address = [];
    }

    if (!user.note) {
      user.note = [];
    }

    this.isNewRecord = !user.firstName && !user.lastName && !user.email && !user.telephone;

    this.user = user;

    console.log(this.user);
  }

  addAddress() {
    this.user.address.push({});
  }

  saveChanges() {
    if (!this.user.saving) {
      this.user.saving = true;

      let user = JSON.parse(JSON.stringify(this.user));

      [
        'managableLocations',
        'customFields',
        'purchases',
      ].forEach((key) => {
        if (user[key]) {
          delete user[key];
        }
      });


      this.ioService.post('/customer/updateCustomer', {
        user,
        // locationId: this.locationService.getActiveLocationId()
      }).then((saveResponse: any) => {
        // this.init();
        this.reportService.loadReport(0);
        this.close();
      }).catch((error: any) => {
        this.user.saving = false;
        if (error.error?.errorKey === 'duplicateUser') {
          this.promptDuplicateUser(error.error, user);
        } else {
          this.user.error = error.error.msg;
        }
      });
    }
  }

  // The server matched an existing person by email, telephone, or exact name.
  // Ask the operator whether this is the same person or a genuinely new one.
  // Name matches may be saved anyway; email/telephone duplicates cannot.
  promptDuplicateUser(duplicate, user) {
    const matchedName = duplicate.matchedName || 'An existing record';
    const matchType = duplicate.matchType || 'information';

    if (duplicate.matchedUserType === 'user') {
      AppComponent.showMessage(
        'Customer Exists as User',
        `${matchedName} already exists as a user with the same ${matchType}. ` +
        'Users can already make purchases as customers, so their existing record should be used.',
        ['Open Existing User', 'Cancel']
      ).afterClosed().subscribe((result: DialogOption) => {
        if (result?.value === 'Open Existing User') {
          this.openExistingRecord('user', duplicate.matchedUserId);
        }
      });
    } else {
      const options = matchType === 'name' ? ['Open Existing', 'New Customer', 'Cancel'] : ['Open Existing', 'Cancel'];
      AppComponent.showMessage(
        'Customer Already Exists',
        `A customer with the same ${matchType} already exists (${matchedName}). Is this the same person or a new customer?`,
        options, true, options.length > 2 ? '760px' : '500px'
      ).afterClosed().subscribe((result: DialogOption) => {
        if (result?.value === 'Open Existing') {
          this.openExistingRecord('customer', duplicate.matchedUserId);
        } else if (result?.value === 'New Customer') {
          this.retrySaveAllowingNameDuplicate(user);
        }
      });
    }
  }

  async openExistingRecord(type, matchedUserId) {
    // If this record is a blank stub from the add flow, remove it before
    // switching to the existing record so it does not linger in the report.
    if (this.isNewRecord) {
      try {
        await this.ioService.post('/customer/deleteCustomer', {
          userId: this.documentId
        });
      } catch (error) {
        console.log('Failed to remove blank customer record', error);
      }
    }

    this.reportService.loadReport(null);
    this.loadDetail(type, matchedUserId);
  }

  retrySaveAllowingNameDuplicate(user) {
    this.user.saving = true;
    this.ioService.post('/customer/updateCustomer', {
      user,
      allowNameDuplicate: true
    }).then(() => {
      this.reportService.loadReport(0);
      this.close();
    }).catch((error: any) => {
      this.user.error = error.error?.msg || 'Failed to save customer.';
      this.user.saving = false;
    });
  }

  mergeProfiles() {
    CustomerMergeComponent.showModal(this.user).afterClosed().subscribe((result: any) => {
      if (result && result.merged) {
        AppComponent.showSnack('Customer profiles merged.', '', 3000);
        this.reportService.loadReport(null);
        if (String(result.targetUserId) === String(this.documentId)) {
          // This profile survived — reload it with the merged data.
          this.init();
        } else {
          // The other profile survived — open it.
          this.loadDetail('customer', result.targetUserId);
        }
      }
    });
  }

  deleteCustomer() {
    if (!this.user.saving) {
      this.user.saving = true;
      this.ioService.post('/customer/deleteCustomer', {
        userId: this.user._id
      }).then((saveResponse: any) => {
        this.reportService.loadReport(null);
        this.close();
      }).catch((error: any) => {
        this.user.error = error.error.msg;
        this.user.saving = false;
      });
    }
  }


  toggleBoolean(toggleTarget) {
    this.user[toggleTarget] = !this.user[toggleTarget];
  }


}
